import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Icon } from '../lib/icons';
import { dataService, recordScan } from '../services/dataService';
import { useTenantBase, toTenantPath, publicSlugFromPath } from '../lib/tenantPath';
import { useLanguage } from '../context/LanguageContext';

function HeroSlider({ slides }) {
  const [i, setI] = useState(0);
  const slide = slides[i];
  return (
    <div className="relative overflow-hidden rounded-card">
      {slides.map((s, idx) => (
        <img
          key={s.id}
          src={s.image}
          alt={s.title}
          loading={idx === 0 ? 'eager' : 'lazy'}
          className={`h-[420px] w-full object-cover transition-opacity duration-700 md:h-[520px] ${idx === i ? 'opacity-100' : 'absolute inset-0 opacity-0'}`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-transparent" />
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-md px-8 md:px-14">
          <h1 className="font-display text-4xl leading-[1.1] text-white md:text-[52px]">{slide.title}</h1>
          <span className="mt-5 block h-px w-12 bg-white/70" />
          <p className="mt-5 font-sans text-base text-white/90 md:text-lg">{slide.text}</p>
        </div>
      </div>
      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
        {slides.map((s, idx) => (
          <button
            key={s.id}
            aria-label={`Vai alla slide ${idx + 1}`}
            onClick={() => setI(idx)}
            className={`h-2 rounded-full transition-all ${idx === i ? 'w-6 bg-white' : 'w-2 bg-white/60'}`}
          />
        ))}
      </div>
    </div>
  );
}

function EventCarousel({ items }) {
  const [i, setI] = useState(0);
  const base = useTenantBase();
  const go = (d) => setI((prev) => (prev + d + items.length) % items.length);
  const item = items[i];
  return (
    <div className="relative mx-auto -mt-16 max-w-3xl px-4 md:-mt-20">
      <div className="relative rounded-card border border-paelbi-line bg-white p-3 shadow-lift">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col justify-center p-4 md:p-6">
            <Badge>{item.badge}</Badge>
            <h3 className="mt-3 font-display text-2xl text-paelbi-ink">{item.title}</h3>
            <p className="mt-2 font-sans text-sm text-paelbi-ink/70">{item.text}</p>
            <Button to={`${base}/eventi`} variant="secondary" icon="arrowRight" className="mt-5 h-11 w-fit px-5 text-sm">
              {item.cta}
            </Button>
          </div>
          <div className="overflow-hidden rounded-[12px]">
            <img src={item.image} alt={item.title} loading="lazy" className="h-48 w-full object-cover md:h-full" />
          </div>
        </div>
        <button aria-label="Precedente" onClick={() => go(-1)} className="absolute -left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paelbi-line bg-white text-paelbi-brown shadow-soft transition-colors hover:bg-paelbi-bg">
          <Icon name="chevronLeft" size={20} />
        </button>
        <button aria-label="Successivo" onClick={() => go(1)} className="absolute -right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paelbi-line bg-white text-paelbi-brown shadow-soft transition-colors hover:bg-paelbi-bg">
          <Icon name="chevronRight" size={20} />
        </button>
      </div>
      <div className="mt-4 flex justify-center gap-2">
        {items.map((it, idx) => (
          <button key={it.id} aria-label={`Evento ${idx + 1}`} onClick={() => setI(idx)} className={`h-2 rounded-full transition-all ${idx === i ? 'w-6 bg-paelbi-brown' : 'w-2 bg-paelbi-accent'}`} />
        ))}
      </div>
    </div>
  );
}

export function PaelbiHome() {
  const [data, setData] = useState(null);
  const base = useTenantBase();
  const { lang } = useLanguage();
  useEffect(() => { dataService.getGuestHome().then(setData); }, [lang]);
  // Track property QR scans (scan-to-book). Guest portal handles its own scans.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const isGuest = /^\/g\//.test(window.location.pathname);
    if (p.get('src') === 'qr' && !isGuest) {
      recordScan({ slug: publicSlugFromPath(window.location.pathname), source: 'property' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!data) return null;

  return (
    <div className="pb-20">
      <section className="pt-8">
        <Container>
          <HeroSlider slides={data.heroSlides} />
        </Container>
      </section>

      <EventCarousel items={data.eventCarousel} />

      {/* CATEGORY TILES */}
      <section className="pt-16">
        <Container>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3">
            {data.categories.map((c) => (
              <Link
                key={c.id}
                to={toTenantPath(c.path, base)}
                className="group flex flex-col items-center justify-center gap-4 rounded-card border border-paelbi-line bg-white px-6 py-10 shadow-card transition-all hover:-translate-y-1 hover:shadow-soft"
              >
                <Icon name={c.icon} size={40} strokeWidth={1.5} className="text-paelbi-brown transition-transform group-hover:scale-105" />
                <span className="font-display text-xl text-paelbi-ink">{c.label}</span>
                <span className="h-px w-8 bg-paelbi-accent" />
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* AMENITIES */}
      <section className="pt-12">
        <Container>
          <div className="rounded-card border border-paelbi-line bg-white p-8 shadow-card">
            <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.08em] text-paelbi-ink/60">Servizi inclusi</h3>
            <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-4 lg:grid-cols-7">
              {data.amenities.map((a) => (
                <div key={a.label} className="flex flex-col items-center gap-2 text-center">
                  <Icon name={a.icon} size={28} strokeWidth={1.5} className="text-paelbi-brown" />
                  <span className="font-sans text-xs text-paelbi-ink/70">{a.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default PaelbiHome;
